import { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { visuallyHidden } from "@mui/utils";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import boardApi from "../../api/boardApi";
import rewardApi from "../../api/rewardApi";
import structureApi from "../../api/structureApi";
import BoardModal from "../../components/common/BoardModal";
import BoardBackgroundModal from "../../components/common/BoardBackgroundModal";
import assets from "../../assets/index";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";
import { setFavouriteList } from "../../redux/features/favouriteSlice";
import { useDispatch, useSelector } from "react-redux";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import imageCompression from "browser-image-compression";
import heic2any from "heic2any";
import boardTypeApi from "../../api/boardTypeApi";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import dayjs from "dayjs";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import WallpaperIcon from "@mui/icons-material/Wallpaper";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

let timer;
const timeout = 500;

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "name",
    numeric: false,
    disablePadding: true,
    label: "Name",
  },
  {
    id: "boardTypeName",
    numeric: false,
    disablePadding: true,
    label: "Tipe Board",
  },
  {
    id: "startDate",
    numeric: false,
    disablePadding: true,
    label: "Start Date",
  },
  {
    id: "dueDate",
    numeric: false,
    disablePadding: true,
    label: "Due Date",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: true,
    label: "Created By",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: true,
    label: "Status Board",
  },
  {
    id: "background",
    numeric: false,
    disablePadding: true,
    label: "Background",
  },
  {
    id: "action",
    numeric: true,
    disablePadding: false,
    label: "Action",
  },
];

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const {
    numSelected,
    showDialogDeleteBoard,
    selected,
    requestSearch,
    searchFilter,
  } = props;
  const theme = useTheme();

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{
            flex: "1 1 100%",
            display: "flex",
            alignItems: "center",
            color:
              theme.palette.mode === "dark"
                ? assets.colors.rolesTextDark
                : assets.colors.rolesTextLight,
          }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          <FormatListBulletedIcon sx={{ mr: 1 }} /> List Board
        </Typography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton onClick={() => showDialogDeleteBoard(selected)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          sx={{
            background:
              theme.palette.mode === "dark"
                ? assets.colors.inputTypeDark
                : assets.colors.inputTypeLight,
            mt: 2,
            mr: 2,
            width: "300px",
          }}
          value={searchFilter}
          size="small"
          margin="normal"
          variant="outlined"
          placeholder="Search name..."
          type="search"
          onInput={(e) => requestSearch(e.target.value)}
        />
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const Boards = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const theme = useTheme();
  const [selectedBoard, setSelectedBoard] = useState(undefined);
  const [selectedBackgroundBoard, setSelectedBackgroundBoard] =
    useState(undefined);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const dense = useState(true);
  const [rows, setRows] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [structure, setStructure] = useState("");
  const dispatch = useDispatch();

  const [fileURL, setFileURL] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteFailCount, setDeleteFailCount] = useState(0);
  const [nameErrText, setNameErrText] = useState("");

  const favouriteList = useSelector((state) => state.favourites.value);
  const [boardTypes, setBoardTypes] = useState([]);

  const [copyList, setCopyList] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const requestSearch = (searched) => {
    setCopyList(
      rows.filter((item) =>
        item.title.toString().toLowerCase().includes(searched)
      )
    );
    setSearchFilter(searched);
  };

  useEffect(() => {
    const getBoardTypes = async () => {
      try {
        const res = await boardTypeApi.getAll();
        setBoardTypes(res);
      } catch (err) {
        console.log(err);
      }
    };
    getBoardTypes();
  }, []);

  const onUpdateBoard = (board) => {
    const newData = [...rows];
    const boardIndex = newData.findIndex((e) => e._id === board.id);
    newData[boardIndex].name = board.name;
    newData[boardIndex].reward = board.reward;
    setRows(newData);
    setCopyList(newData);
  };

  const onUpdateBoardData = (board) => {
    const newData = [...rows];
    const boardIndex = newData.findIndex((e) => e._id === board.id);
    newData[boardIndex].boardTypeName =
      board.boardType !== undefined ? board.boardType.name : "";
    newData[boardIndex].title = board.title;
    newData[boardIndex].favourite = board.favourite;
    newData[boardIndex].description = board.description;
    newData[boardIndex].startDate = dayjs(board.startDate).format(
      "DD MMM YYYY"
    );
    newData[boardIndex].dueDate = dayjs(board.dueDate).format("DD MMM YYYY");
    newData[boardIndex].background = (
      <img
        src={board.background}
        alt={board.background}
        height="50"
        style={{ margin: "5px" }}
      />
    );
    setRows(newData);
    setCopyList(newData);
  };

  const deleteBoards = (boards) => {
    boards.forEach(async (value) => {
      try {
        const deleteJson = await boardApi.delete(value);
        const newData = [...rows].filter((e) => !boards.includes(e.id));
        setRows(newData);
        setCopyList(newData);
        setSelected([]);
        let newFavouriteList = [...favouriteList];
        newFavouriteList = newFavouriteList.filter((e) => e.id !== value);
        dispatch(setFavouriteList(newFavouriteList));

        hideDialog();
      } catch (err) {
        console.log(err);
      }
    });

    navigate(0);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  async function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
    });
  }

  const updateImage = async (e) => {
    clearTimeout(timer);
    const image = await getBase64(fileURL)
      .then((res) => res)
      .catch((err) => err);
    timer = setTimeout(async () => {
      try {
        await boardApi.update(user.id, { background: image });
      } catch (err) {
        console.log(err);
      }
    }, timeout);
    // user.image = image;
    // props.onUpdate(user);
    setFileURL(null);
  };

  const convertAndCompressHeic = async (file, type) => {
    const blob = file;
    try {
      setLoading(true);
      const convertedBlob = await heic2any({
        blob,
        toType: "image/jpeg",
        quality: 0.5,
      });
      const convertedFile = new File([convertedBlob], file.name, {
        type: convertedBlob.type,
      });
      setFileURL(convertedFile);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const compressNonHeic = async (file, type) => {
    try {
      setLoading(true);
      const compressedBlob = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 100,
        useWebWorker: true,
      });
      const convertedFile = new File([compressedBlob], file.name, {
        type: compressedBlob.type,
      });
      setFileURL(convertedFile);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  const uploadImage = async (e) => {
    const newName = e.target.files[0];
    if (newName.type === "image/heic") {
      convertAndCompressHeic(newName, "image");
    } else {
      compressNonHeic(newName, "image");
    }
  };

  const addFavourite = async (board) => {
    try {
      const boardId = board.id;
      const updatedBoard = await boardApi.update(boardId, {
        favourite: !board.favourite,
      });
      let newFavouriteList = [...favouriteList];
      if (board.favourite) {
        newFavouriteList = newFavouriteList.filter((e) => e.id !== boardId);
      } else {
        newFavouriteList.unshift(updatedBoard);
      }
      dispatch(setFavouriteList(newFavouriteList));
      const newData = [...rows];
      console.log(newData);
      const boardIndex = newData.findIndex((e) => e._id === board._id);
      console.log(boardIndex);
      newData[boardIndex].title = board.title;
      newData[boardIndex].favourite = !board.favourite;
      setRows(newData);
      setCopyList(newData);
      hideDialog();
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const getRewards = async () => {
      try {
        const res = await rewardApi.getAll();
        setRewards(res);
      } catch (err) {
        console.log(err);
      }
    };
    getRewards();
  }, []);

  useEffect(() => {
    const getBoards = async () => {
      try {
        const jsonData = [];
        let res = [];
        if (user.role.typeManage === 1) {
          res = await boardApi.getAllUserId(user.id);
        } else {
          res = await boardApi.getAll();
        }
        res.forEach((value, index) => {
          let boardTypeName = "";
          if (value.boardType !== undefined) {
            boardTypeName = value.boardType.name;
          }
          jsonData.push({
            _id: value.id,
            id: value.id,
            icon: value.icon,
            title: value.title,
            background: (
              <>
                <img
                  src={value.background}
                  alt={value.background}
                  height="50"
                  style={{ margin: "5px" }}
                />
              </>
            ),
            boardTypeName: boardTypeName,
            userName: value.user.name,
            startDate: dayjs(value.startDate).format("DD MMM YYYY"),
            dueDate: dayjs(value.dueDate).format("DD MMM YYYY"),
            status: (
              <IconButton
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  showDialogUpdateStatus(value);
                }}
              >
                {value.favourite ? (
                  <CircleIcon color="success" />
                ) : (
                  <CircleIcon color="error" />
                )}
              </IconButton>
            ),
            action: (
              <>
                {/* <Tooltip title="Setting Reward">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBoard(value);
                    }}
                  >
                    <ModeEditOutlineOutlinedIcon />
                  </IconButton>
                </Tooltip> */}
                <Tooltip title="Setting Reward">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBoard(value);
                    }}
                  >
                    <EmojiEventsIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Board">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBackgroundBoard(value);
                    }}
                  >
                    <ModeEditOutlineOutlinedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clone Board">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      showDialogCloneBoard(value.id);
                    }}
                  >
                    <FileCopyIcon />
                  </IconButton>
                </Tooltip>
              </>
            ),
          });
        });
        setRows(jsonData);
        setCopyList(jsonData);
      } catch (err) {
        console.log(err);
      }
    };
    getBoards();
  }, [favouriteList]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = useMemo(
    () =>
      stableSort(rows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, rows]
  );

  const visibleRowsCopy = useMemo(
    () =>
      stableSort(copyList, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, copyList]
  );

  const [openDialogDeleteBoard, setOpenDialogDeleteBoard] = useState(false);
  const [openDialogCloneBoard, setOpenDialogCloneBoard] = useState(false);
  const [openDialogUpdateStatus, setOpenDialogUpdateStatus] = useState(false);
  const [idUpdateStatus, setIdUpdateStatus] = useState(false);
  const [idDeleteBoard, setIdDeleteBoard] = useState(false);

  const showDialogDeleteBoard = (sectionId) => {
    setOpenDialogDeleteBoard(true);
    setIdDeleteBoard(sectionId);
  };

  const showDialogCloneBoard = (sectionId) => {
    setOpenDialogCloneBoard(true);
    setIdDeleteBoard(sectionId);
  };

  const cloneBoard = (boardId) => {
    console.log(boardId);
    clearTimeout(timer);
    timer = setTimeout(async () => {
      try {
        const value = await boardApi.getCloneBoard(boardId);

        const jsonData = [];
        let boardTypeName = "";
        if (value.boardType !== undefined) {
          boardTypeName = value.boardType.name;
        }
        jsonData.push({
          _id: value.id,
          id: value.id,
          icon: value.icon,
          title: value.title,
          background: (
            <>
              <img
                src={value.background}
                alt={value.background}
                height="50"
                style={{ margin: "5px" }}
              />
            </>
          ),
          boardTypeName: boardTypeName,
          userName: value.user.name,
          startDate: dayjs(value.startDate).format("DD MMM YYYY"),
          dueDate: dayjs(value.dueDate).format("DD MMM YYYY"),
          status: (
            <IconButton
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                showDialogUpdateStatus(value);
              }}
            >
              {value.favourite ? (
                <CircleIcon color="success" />
              ) : (
                <CircleIcon color="error" />
              )}
            </IconButton>
          ),
          action: (
            <>
              {/* <Tooltip title="Setting Reward">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBoard(value);
                    }}
                  >
                    <ModeEditOutlineOutlinedIcon />
                  </IconButton>
                </Tooltip> */}
              <Tooltip title="Setting Reward">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBoard(value);
                  }}
                >
                  <EmojiEventsIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Board">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBackgroundBoard(value);
                  }}
                >
                  <ModeEditOutlineOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clone Board">
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    showDialogCloneBoard(value.id);
                  }}
                >
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
            </>
          ),
        });

        let newRows = [...rows];
        newRows.unshift(jsonData);
        setRows(newRows);
        setCopyList(newRows);

        if (value.favourite) {
          navigate(0);
          // let newFavouriteList = [...favouriteList];
          // newFavouriteList.unshift(value);
          // dispatch(setFavouriteList(newFavouriteList));
        }

        hideDialog();
      } catch (err) {
        console.log(err);
      }
    }, timeout);
  };

  const showDialogUpdateStatus = (sectionId) => {
    setOpenDialogUpdateStatus(true);
    setIdUpdateStatus(sectionId);
  };

  const hideDialog = () => {
    setOpenDialogDeleteBoard(false);
    setOpenDialogCloneBoard(false);
    setOpenDialogUpdateStatus(false);
  };

  return (
    <>
      {openDialogDeleteBoard && (
        <Dialog
          open={openDialogDeleteBoard}
          onClose={hideDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure delete board
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => deleteBoards(idDeleteBoard)} color="primary">
              Yes
            </Button>
            <Button onClick={hideDialog} color="primary">
              No
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openDialogCloneBoard && (
        <Dialog
          open={openDialogCloneBoard}
          onClose={hideDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure clone board
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => cloneBoard(idDeleteBoard)} color="primary">
              Yes
            </Button>
            <Button onClick={hideDialog} color="primary">
              No
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {openDialogUpdateStatus && (
        <Dialog
          open={openDialogUpdateStatus}
          onClose={hideDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure update status board
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => addFavourite(idUpdateStatus)}
              color="primary"
            >
              Yes
            </Button>
            <Button onClick={hideDialog} color="primary">
              No
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Box
        sx={{
          // width: "100%",
          margin: "5px 25px 0px 25px",
        }}
      >
        <Typography
          sx={{
            padding: 0,
            fontSize: "1.5rem",
            fontWeight: "500",
            color:
              theme.palette.mode === "dark"
                ? assets.colors.rolesTextDark
                : assets.colors.rolesTextLight,
          }}
        >
          🗓️ Manage Boards
        </Typography>
        <Typography
          sx={{
            padding: 0,
            marginTop: "2px",
            fontSize: "14px",
            fontWeight: "400",
            color:
              theme.palette.mode === "dark"
                ? assets.colors.rolesTextDark
                : assets.colors.rolesTextLight,
          }}
        >
          <i>Manage all boards in the zabran scrum board application</i>
        </Typography>
      </Box>
      <Box
        sx={{
          // width: "100%",
          m: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Paper
          sx={{
            width: "100%",
            border:
              theme.palette.mode === "dark"
                ? "1px solid " + assets.colors.borderDark
                : "1px solid " + assets.colors.borderLight,
          }}
        >
          <EnhancedTableToolbar
            numSelected={selected.length}
            showDialogDeleteBoard={showDialogDeleteBoard}
            requestSearch={requestSearch}
            searchFilter={searchFilter}
            selected={selected}
          />
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
              {(visibleRowsCopy.length > 0 ? visibleRowsCopy : visibleRows).map((row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": labelId,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.title}
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.boardTypeName}
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.startDate}
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.dueDate}
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.userName}
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.status}
                      </TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.background}
                      </TableCell>
                      <TableCell align="right">{row.action}</TableCell>
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      <BoardModal
        board={selectedBoard}
        rewards={rewards}
        onClose={() => setSelectedBoard(undefined)}
        onUpdate={onUpdateBoard}
      />

      <BoardBackgroundModal
        boardTypes={boardTypes}
        board={selectedBackgroundBoard}
        onClose={() => setSelectedBackgroundBoard(undefined)}
        onUpdate={onUpdateBoardData}
      />
    </>
  );
};

export default Boards;
